import { useState } from "react";
import { getForwardedParams, currentHrefWithForwardedUtm } from "~/utils/utm";

const HUBSPOT_PORTAL_ID = "9161697";
const HUBSPOT_FORM_ID = "a080248a-fad5-4e84-a0ed-e74f5e8496ec";
const HUBSPOT_API = `https://api.hsforms.com/submissions/v3/integration/submit/${HUBSPOT_PORTAL_ID}/${HUBSPOT_FORM_ID}`;
const SUCCESS_MESSAGE = "Thanks! We can't wait to share more with you. We'll be in touch.";

/**
 * Footer newsletter signup form.
 * Submits directly to HubSpot API (matching Hugo's hubspot.js pattern).
 * Handles GCLID extraction for Google Ads tracking.
 *
 * `hideLabel`: for reuse somewhere narrower than the footer row this was
 * built for (e.g. a page section that already states what the form is for
 * in its own heading) — the "Subscribe to the newsletter" text assumes
 * room to sit inline with a fixed-width input, and just wraps awkwardly
 * in a narrow column instead.
 */
export function NewsletterForm({ hideLabel = false }: { hideLabel?: boolean } = {}) {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || submitting) return;

    setSubmitting(true);
    setError("");

    try {
      // Attribution: forward the session-persisted tracking params. gclid is a
      // first-class field; utm_* ride along on the enriched pageUri so HubSpot's
      // native analytics attributes the campaign without depending on utm_*
      // contact properties existing in the portal.
      const tracking = getForwardedParams();

      const fields: Array<{ name: string; value: string }> = [
        { name: "email", value: email },
      ];

      if (tracking.gclid) {
        fields.push({ name: "GCLID", value: tracking.gclid });
      }

      const pageUri =
        currentHrefWithForwardedUtm() ||
        (typeof window !== "undefined" ? window.location.href : "");

      const res = await fetch(HUBSPOT_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fields,
          context: {
            pageUri,
            pageName: typeof document !== "undefined" ? document.title : "",
          },
        }),
      });

      if (res.ok) {
        setSubmitted(true);
      } else {
        setError("Something went wrong. Please try again.");
      }
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:gap-6">
        <p className="text-gray-30">{SUCCESS_MESSAGE}</p>
      </div>
    );
  }

  return (
    <div
      className={
        hideLabel
          ? "flex flex-col gap-3"
          : "flex flex-col gap-3 lg:flex-row lg:items-center lg:gap-6"
      }
    >
      {!hideLabel && (
        <p className="font-display text-white">Subscribe to the newsletter</p>
      )}
      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-3 md:flex-row md:items-center"
        // HubSpot's collected-forms script tags forms with data-hs-cf-bound
        // in the DOM before React hydrates, which otherwise trips a hydration
        // attribute-mismatch warning. This is expected third-party behavior.
        suppressHydrationWarning
      >
        <label>
          <span className="sr-only">Email</span>
          <input
            type="email"
            name="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
            required
            className="w-full rounded-lg border-0 bg-white/10 px-4 py-1.5 font-body text-white placeholder:text-dark-border focus:ring-0 md:w-[16.25rem]"
          />
        </label>
        <button
          type="submit"
          disabled={submitting}
          className="w-full rounded-lg bg-teal-cta px-6 py-1.5 font-body font-medium text-white hover:bg-teal-cta-hover disabled:opacity-50 md:w-auto"
        >
          {submitting ? "..." : "Subscribe"}
        </button>
      </form>
      {error && <p className="text-sm text-red">{error}</p>}
    </div>
  );
}
